<template>
  <div v-loading="fetchInProgress" class="program-editor">
    <el-form
      ref="formRef"
      label-position="left"
      label-width="240px"
      :model="form"
      :rules="formRules"
    >
      <el-form-item label="Name">
        <el-input v-model="form.name" />
      </el-form-item>
      <el-form-item label="Type" v-if="isLegacy || user.isAdmin">
        <el-radio v-model="form.type" class="block mb-4" label="BOTH">
          Profile and form
          <span class="ml-2 text-gray-500">
            Startup would apply with both form and profile
          </span>
        </el-radio>
        <el-radio v-model="form.type" class="block my-4" label="FORM">
          Form
          <span class="ml-2 text-gray-500">
            Startup would have their profile locked until application is
            submitted
          </span>
        </el-radio>
        <el-radio v-model="form.type" class="block my-4" label="PROFILE">
          Profile
          <span class="ml-2 text-gray-500">
            Startup would need to fill in their profile only
          </span>
        </el-radio>
      </el-form-item>
      <el-form-item label="Automatic reminders to startups">
        <el-radio-group v-model="form.send_reminders">
          <el-radio :label="true"> Yes </el-radio>
          <el-radio :label="false"> No </el-radio>
        </el-radio-group>
      </el-form-item>
      <el-form-item label="Is there a deadline to apply?" prop="has_end_date">
        <el-radio-group
          v-model="form.has_end_date"
          @change="onHasEndDateChange"
        >
          <el-radio :label="true"> Yes </el-radio>
          <el-radio :label="false"> No </el-radio>
        </el-radio-group>
      </el-form-item>
      <el-form-item
        v-if="form.has_end_date"
        label="Applications deadline"
        prop="ends_at"
      >
        <el-date-picker
          v-model="form.ends_at"
          type="date"
          placeholder="Pick a day"
        />
      </el-form-item>

      <div class="max-w-full">
        <el-form-item>
          <template slot="label">
            <span>Application Page Text</span>
            <el-tooltip placement="right">
              <div slot="content">
                <h6 class="mt-8 text-lg font-bold text-center">
                  This text will be used as a header on your application
                  register page
                </h6>
                <img src="/static/img/program-application.svg" />
              </div>
              <span class="ml-2 ion ion-help-circled"></span>
            </el-tooltip>
          </template>
          <el-input v-model="form.applyText" maxlength="70" show-word-limit />
        </el-form-item>
      </div>
      <el-form-item label="Welcome message">
        <div class="p-4 leading-4 border focus:border-yellow-500">
          <TextEditor
            :text="form.description"
            @blur="form.description = $event"
          />
        </div>
      </el-form-item>
    </el-form>

    <div class="row">
      <div class="col-sm-12 col-md-8">
        <div v-show="showQuestions">
          <h5 class="margin-top-30">Questions</h5>

          <p v-if="!form.questions.length" class="text-muted">
            No questions yet
          </p>

          <draggable
            v-model="form.questions"
            :options="dragOptions"
            :scroll-sensitivity="100"
            :force-fallback="true"
          >
            <ProgramEditorQuestion
              v-for="(question, idx) in form.questions"
              :key="idx"
              :idx="idx"
              :question="question"
              :question-errors="getTextErrorsByQuestionIndex(idx)"
              @question-updated="updateQuestion"
              @remove-clicked="removeQuestion(idx)"
            />
          </draggable>

          <ConfirmModal
            ref="confirmRemoveModal"
            confirm-text="Remove"
            @confirm="confirmQuestionRemoval()"
          >
            <h4 slot="modal-header" class="modal-title text-danger">
              Remove question
            </h4>
            <div slot="modal-body">
              <p>
                Are you sure you want to remove Question
                {{ pendingRemovalQuestionId + 1 }}?
              </p>
            </div>
          </ConfirmModal>
        </div>
      </div>
    </div>

    <div class="row margin-top-30">
      <div class="col-sm-12 col-md-8">
        <div v-show="showQuestions" style="display: inline-block">
          <button
            class="btn btn-info"
            @click.prevent="addQuestion('COMPANY_PROFILE')"
          >
            Add Frequently Asked Questions
          </button>

          <CompanyProfileQuestionsPickerModal
            ref="companyProfileQuestionsPicker"
            :already-selected="profileQuestionsAttrs"
            @ok="addCompanyProfileQuestions"
          />

          <Modal
            ref="previewModal"
            title="Questions preview"
            :has-ok-button="false"
            xlarge
          >
            <div slot="modal-body">
              <ProgramForm
                ref="previewForm"
                :editable="false"
                :questions="form.questions"
              />
            </div>
          </Modal>

          <vb-dropdown type="info">
            <button
              slot="before"
              type="button"
              class="btn btn-info"
              @click.prevent="addQuestion()"
            >
              Add Custom Question
            </button>
            <li v-for="type in QUESTION_TYPES" :key="type.value">
              <a href @click.prevent="addQuestion(type.value)">
                {{ type.label }}
              </a>
            </li>
          </vb-dropdown>

          <button class="btn btn-info" @click.prevent="showPreview()">
            Preview
          </button>
        </div>

        <button
          class="btn btn-primary pull-right"
          :disabled="saveInProgress"
          @click.prevent="save()"
        >
          Save
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import autosize from '@/directives/autosize'
import draggable from 'vuedraggable'
import TextEditor from '@/components/TextEditor'
import { mapState } from 'vuex'
import _ from 'lodash'
import ProgramOrganizationIdMixin from '@/modules/dashboard-organizer/ProgramOrganizationIdMixin'
import ProgramEditorQuestion from '@/modules/dashboard-organizer/ProgramEditorQuestion'
import CompanyProfileQuestionsPickerModal from '@/modules/dashboard-organizer/CompanyProfileQuestionsPickerModal'
import CompanyProfileQuestions from '@/modules/dashboard-organizer/CompanyProfileQuestions'
import ProgramForm from '@/modules/dashboard-organizer/ProgramForm'
import * as utils from '@/utils'

const PROFILE = 'PROFILE'
const FORM = 'FORM'
const BOTH = 'BOTH'
const types = { PROFILE, FORM, BOTH }

export default {
  directives: { autosize },
  components: {
    draggable,
    ProgramEditorQuestion,
    TextEditor,
    CompanyProfileQuestionsPickerModal,
    ProgramForm,
  },
  mixins: [ProgramOrganizationIdMixin],
  computed: {
    dragOptions() {
      return {
        handle: '.handle',
        scroll: true, // Enable the plugin. Can be HTMLElement.
        forceAutoscrollFallback: false, // force autoscroll plugin to enable even when native browser autoscroll is available
        scrollSensitivity: 200, // px, how near the mouse must be to an edge to start scrolling.
        scrollSpeed: 10, // px, speed of the scrolling
        bubbleScroll: true, // apply autoscroll to all parent elements, allowing for easier movement
      }
    },
    profileQuestionsAttrs() {
      return this.form.questions
        .filter((q) => q.profile_attr)
        .map((q) => q.profile_attr)
    },
    types: () => types,
    ...mapState({
      user: (state) => state.user.data,
      apiErrors: (state) => state.organizationProgramDetails.errors,
      fetchInProgress: (state) =>
        state.user.fetchInProgress ||
        state.organizationProgramDetails.fetchInProgress,
      saveInProgress: (state) =>
        state.organizationProgramDetails.saveInProgress,
    }),
    showQuestions() {
      return !this.fetchInProgress && this.form.type !== 'PROFILE'
    },
    formRules() {
      const self = this
      return {
        ends_at: [
          {
            validator(rule, value, callback) {
              if (!value && self.form.has_end_date) {
                callback(new Error('This field is required'))
              }
              callback()
            },
          },
        ],
      }
    },
    isNewType() {
      const createdAt = new Date(this.program.created_at)
      const newTypeStartDate = new Date('2021-08-11T00:00:00Z')
      return createdAt > newTypeStartDate && this.program.type === FORM
    },
    isLegacy() {
      return !this.isNewType
    },
  },
  data() {
    return {
      pendingRemovalQuestionId: null,
      formPreviewVisible: false,
      form: {
        name: '',
        applyText: '',
        description: '',
        type: BOTH,
        ends_at: null,
        questions: [],
        has_end_date: true,
        send_reminders: true,
      },
      QUESTION_TYPES: [
        { label: 'Text', value: 'TEXT' },
        { label: 'Number', value: 'NUMBER' },
        { label: 'Single selection', value: 'SINGLE_CHOICES' },
        { label: 'Multiple selection', value: 'MULTIPLE_CHOICES' },
        { label: 'Image', value: 'IMAGE' },
        { label: 'Attachment', value: 'ATTACHMENT' },
        { label: 'Agreement', value: 'AGREEMENT' },
      ],
    }
  },
  watch: {
    program() {
      const program = this.program
      this.form.name = program.name
      this.form.applyText = program.apply_text
      this.form.ends_at = program.ends_at
      this.form.description = program.description
      this.form.lockStartupProfiles = program.lock_startup_profiles
      this.form.questions = program.questions.map((q) => {
        const choices = (q.choices || []).map((c) => Object.assign({}, c))
        return Object.assign({}, q, { choices })
      })
      this.form.type = program.type
      this.form.has_end_date = program.has_end_date
      this.form.send_reminders = program.send_reminders
    },
  },
  methods: {
    getTextErrorsByQuestionIndex(idx) {
      if (this.apiErrors.questions && this.apiErrors.questions[idx]) {
        return this.apiErrors.questions[idx].text
      } else {
        return []
      }
    },

    addQuestion(type = 'TEXT') {
      if (type === 'COMPANY_PROFILE') {
        const picker = this.$refs.companyProfileQuestionsPicker
        picker.open()
        // picker.setSelected([])
      } else {
        this.form.questions.push({
          id: utils.uuid4(),
          text: '',
          type: type,
          required: false,
          choices: [],
        })
      }
    },

    onQuestionDelete(idx) {
      const question = this.form.questions[idx]
      if (!question.text) {
        this.form.questions = this.form.questions.filter((q, i) => i !== idx)
        if (idx >= 1) {
          this.focusAtQuestion(idx - 1)
        }
      }
    },

    focusAtQuestion(idx) {
      const self = this
      setTimeout(() => {
        self.$el.getElementsByClassName('question-input')[idx].focus()
      }, 0)
    },

    async save() {
      try {
        await this.$refs.formRef.validate()
      } catch (err) {
        this.$message.warning('Please correct form errors first')
        return
      }

      const data = {
        id: this.program.id,
        name: this.form.name,
        description: this.form.description,
        apply_text: this.form.applyText,
        type: this.form.type,
        ends_at: this.form.ends_at,
        questions: this.form.questions.map((q) => {
          const question = _.cloneDeep(q)
          if (!question.id) {
            question.id = utils.uuid4()
          }
          question.choices = (q.choices || [])
            .filter((choice) => choice.value)
            .map((choice) => {
              const newChoice = Object.assign({}, choice)
              if (!choice.label) {
                newChoice.label = choice.value
              }
              return newChoice
            })
          return question
        }),
        onboarding_mail_subject: this.program.onboarding_mail_subject,
        onboarding_mail_text: this.program.onboarding_mail_text,
        has_end_date: this.form.has_end_date,
        send_reminders: this.form.send_reminders,
      }
      try {
        await this.$store.dispatch('organizationProgramDetails/save', {
          organizationId: this.realOrganizationId,
          data: data,
        })
        this.$message.success('Saved')
      } catch (error) {
        this.$message.error('Unexpected error')
      }
    },

    togglePreview() {
      this.formPreviewVisible = !this.formPreviewVisible
    },

    formDidChange(data) {
      console.log(' => formDidChange', data)
    },

    removeQuestion(idx) {
      this.pendingRemovalQuestionId = idx
      this.$refs.confirmRemoveModal.open()
    },
    updateQuestion({ idx, value }) {
      console.log(value)
    },
    confirmQuestionRemoval() {
      this.form.questions = this.form.questions.filter(
        (q, idx) => idx !== this.pendingRemovalQuestionId,
      )
      this.pendingRemovalQuestionId = null
      this.$refs.confirmRemoveModal.close()
    },

    addCompanyProfileQuestions(attrs) {
      console.log(' => addCompanyProfileQuestions attrs:', attrs)
      // this.$refs.companyProfileQuestionsPicker.close()
      attrs.forEach((attr) => {
        if (!this.hasProfileQuestion(attr)) {
          const question = getQuestionForAttr(attr)
          this.form.questions.push(question)
        } else {
          console.warn(` => There is already a question about ${attr}`)
        }
      })
    },

    hasProfileQuestion(attr) {
      return this.form.questions.some((q) => q.profile_attr === attr)
    },

    showPreview() {
      // this.$refs.previewForm.reset()
      this.$refs.previewModal.open()
    },

    onHasEndDateChange(value) {
      if (!value) {
        this.form.ends_at = null
      }
    },
  },
}

function getQuestionForAttr(attr) {
  const question = CompanyProfileQuestions[attr]
  question.read_only = true
  question.profile_attr = attr
  question.required = true
  return question
}
</script>
